import type { AutoCompleteResult } from '@awazevr/design-system.components.autocomplete'
import {
  DataLayerEventType,
  DataLayerFactory,
  type TmsContextType,
} from '@awazevr/mfe-common.contexts.tms'

import { OPTIMIZELY_EVENTS } from '@/constants'
import type { OptimizelyState } from '@/context/optimizely-decisions-context'
import { dataLayerErrorHandler } from '@/utils/dataLayerErrorHandler'
import { trackClientSideOptimizelyEvent } from '@/utils/optimizely/trackOptimizelyEvent'

import type { PlacesSuggestions, Suggestion } from './SearchForm.types'

const fireLocationSuggestionOptimizelyEvent = (
  placesResult: Suggestion[],
  optimizelyState?: OptimizelyState,
): void => {
  if (!optimizelyState) {
    return
  }
  const hasInternalResults =
    placesResult.length > 0 && !placesResult[0].type?.includes('GooglePlaces')
  trackClientSideOptimizelyEvent(
    hasInternalResults
      ? OPTIMIZELY_EVENTS.INTERNAL_LOCATION_SUGGESTION_SHOWN
      : OPTIMIZELY_EVENTS.INTERNAL_LOCATION_SUGGESTION_NO_RESULTS,
    optimizelyState,
  )
}

const fireAutoSuggestTrackingEvents = ({
  searchTerm,
  accommodationType,
  destinations,
  properties,
  noResult,
  tms,
}: {
  searchTerm: string
  accommodationType?: string
  destinations: string[]
  properties: string[]
  noResult: boolean
  tms?: TmsContextType
}): void => {
  if (!tms) {
    return
  }

  if (noResult) {
    tms.updateDataLayer('link', 'auto_suggest_noresult', {
      search_term: searchTerm,
    })
    return
  }

  new DataLayerFactory()
    .getEvent(DataLayerEventType.AutoSuggestImpression)
    .update(
      tms,
      {
        searchTerm,
        accommodationType,
        destinations,
        properties,
      },
      dataLayerErrorHandler,
    )
}

export const handleAutoSuggestSearch = (
  getSuggestions: PlacesSuggestions,
  tms?: TmsContextType,
  optimizelyState?: OptimizelyState,
): ((
  searchTerm: string | null,
  accommodationType?: string,
) => Promise<AutoCompleteResult[]>) => {
  return async (
    searchTerm: string | null,
    accommodationType?: string,
  ): Promise<AutoCompleteResult[]> => {
    if (!searchTerm) {
      return []
    }

    const placesResult = await getSuggestions(searchTerm, accommodationType)

    const destinationResults = placesResult.filter((r) => !r.property)
    const propertyResults = placesResult.filter((r) => r.property)
    const autoSuggestResults = [...destinationResults, ...propertyResults]

    fireAutoSuggestTrackingEvents({
      searchTerm,
      accommodationType,
      destinations: destinationResults.map(({ name }) => name),
      properties: propertyResults.map(({ name }) => name),
      noResult: autoSuggestResults.length === 0,
      tms,
    })

    fireLocationSuggestionOptimizelyEvent(destinationResults, optimizelyState)

    return autoSuggestResults
  }
}
